import { Box } from "@mui/material";
import React from "react";
import SharingsLegalEntity from "../../../../../processingActivity/sharingOfDataStep/sharingsLegalEntityBox/sharingsLegalEntity";
import { DotLegalMultiSelect, DotLegalSelect, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../../../localization/useTranslation";
import { SelectableDocumentItem, SelectableLegalUnitsItem } from "../../../../../processingActivity/ProcessingActivity.types";
import { DotLegalSelectOption } from "../../../../../common/components/dotLegalMultiSelect/DotLegalMultiSelect.types";
import { showTia, showTransferBasis } from "../../../../../legalEntity/isInsecureThirdCountry";
import { SystemModel, SystemSecondarySupplierViewModel } from "../../../../../system/systemEdit/SystemEdit.types";
import SegmentStep from "../../../../../processingActivity/stepSegment/StepSegment";
import {
    filterDocumentDataByCompanyId,
    getSelectableTiaDocumentItems,
    getSelectedDocument,
} from "../../../../../processingActivity/hooks/useDataProcessingAgreement";
import { filterTransferBasis } from "../../../../../legalBasis/TransferBasis.Helper";
import useSystemSecondarySupplierHooks from "./SystemSecondarySupplier.hooks";
import { useSystemSecondarySupplierStyles } from "./SystemSecondarySupplier.styles";
import { ValidationError } from "../../../../../common/validationError";
import TextFieldWithLink from "../../../../../common/components/textFieldWithLink/TextFieldWithLink";
import { SystemSupplierPermissions } from "../../../../EntityTemplate.types";
import { CreateLegalEntityReturnType } from "../../../../../legalEntity/legalEntities/LegalEntities.types";

export interface ISystemSecondarySupplier {
    documentsData: Array<SelectableDocumentItem> | undefined;
    validationErrors: Array<ValidationError>;
    legalEntitiesLoading: boolean;
    legalEntityData: Array<SelectableLegalUnitsItem> | undefined;
    transferBasisLoading: boolean;
    transferBasisData: Array<DotLegalSelectOption> | undefined;
    secondarySupplierIndex: number;
    documentsLoading: boolean;
    countriesLoading: boolean;
    countryList: Array<DotLegalSelectOption>;
    secondarySupplier: SystemSecondarySupplierViewModel;
    readOnly?: boolean;
    system: SystemModel;
    supplierId: string;
    supplierIsDataProcessor: boolean;
    supplierPermissions: SystemSupplierPermissions;
}

function SystemSecondarySupplier(props: ISystemSecondarySupplier) {
    const { translateString } = useTranslation();
    const { secondarySupplierUpdater } = useSystemSecondarySupplierHooks(props);
    const styles = useSystemSecondarySupplierStyles();

    const getNoteTextField = () => {
        return (
            <DotLegalTextField
                label={translateString("noteToSubContractor")}
                value={secondarySupplier.note ?? ""}
                onChange={secondarySupplierUpdater.onNoteChange}
                debounce
                disabled={props.readOnly}
            />
        );
    };

    let secondarySupplier = props.secondarySupplier;
    let selectedSecondarySupplier = props.legalEntityData?.find((x) => x.id === secondarySupplier.dataProcessorId);
    let showTransferBasisFld = showTransferBasis(props.legalEntityData, selectedSecondarySupplier?.id);

    const { hasTiaAndTransferBasis, hasDataProcessorAgreementPermission, hasStorageLocationPermission, hasSupportAccessPermission } = props.supplierPermissions;

    return (
        <React.Fragment>
            <Box sx={styles.subProcessorContainer} key={props.secondarySupplierIndex}>
                <Box justifyContent="center" display="flex">
                    <SegmentStep size="small">
                        {props.secondarySupplier.isLink ? (
                            <>
                                <TextFieldWithLink
                                    label={translateString("linkToSecondarySupplier")}
                                    disabled={props.readOnly}
                                    link={props.secondarySupplier.link}
                                    onLinkChange={secondarySupplierUpdater.onLinkChange}
                                />

                                {getNoteTextField()}
                            </>
                        ) : (
                            <React.Fragment>
                                <SharingsLegalEntity
                                    isLoading={props.legalEntitiesLoading}
                                    selectedItem={secondarySupplier.dataProcessorId}
                                    placeholder={translateString("whoIsSecondarySupplier")}
                                    label={translateString("secondarySupplier")}
                                    options={props.legalEntityData}
                                    disabled={props.readOnly}
                                    toolTipText={translateString("addSecondarySupplierHelperText")}
                                    onLegalEntityChange={(dataProcessorId) => secondarySupplierUpdater.onDataProcessorChange(dataProcessorId)}
                                    showAddNewEntityButton
                                    isThirdCountry={showTransferBasisFld}
                                    errorText={
                                        props.validationErrors.find((v) => v.field === `${props.supplierId}${secondarySupplier.id}dataProcessorId`)?.error
                                    }
                                    createAndNavigateName={props.system.name}
                                    createAndNavigateReturnType={CreateLegalEntityReturnType.Asset}
                                />
                                {getNoteTextField()}

                                {hasDataProcessorAgreementPermission && (
                                    <DotLegalSelect
                                        selectedItem={secondarySupplier.hasDataProcessorAgreement.toString()}
                                        label={translateString("agreement")}
                                        placeholder={translateString("agreement")}
                                        disabled={props.readOnly}
                                        disableClearable
                                        onChange={(option) => secondarySupplierUpdater.onAgreementChange(option ? option.toLowerCase() === "true" : false)}
                                        options={[
                                            {
                                                name: translateString("yes"),
                                                id: "true",
                                            },
                                            { name: translateString("no"), id: "false" },
                                        ]}
                                        toolTipText={translateString("sharingAgreementHelperText")}
                                        noOptionsLabel={translateString("noOptions")}
                                    />
                                )}

                                {props.supplierIsDataProcessor && (
                                    <DotLegalSelect
                                        selectedItem={secondarySupplier.isSubDataProcessor.toString()}
                                        label={translateString("Subprocessor")}
                                        placeholder={translateString("Subprocessor")}
                                        disabled={props.readOnly}
                                        disableClearable
                                        onChange={(option) =>
                                            secondarySupplierUpdater.onIsSubDataProcessorChange(option ? option.toLowerCase() === "true" : false)
                                        }
                                        options={[
                                            {
                                                name: translateString("yes"),
                                                id: "true",
                                            },
                                            { name: translateString("no"), id: "false" },
                                        ]}
                                        toolTipText={translateString("secondarySupplierIsSubProcessorHelperText")}
                                        noOptionsLabel={translateString("noOptions")}
                                    />
                                )}

                                {props.secondarySupplier.isSubDataProcessor && (
                                    <React.Fragment>
                                        {showTransferBasisFld && hasTiaAndTransferBasis && (
                                            <DotLegalSelect
                                                selectedItem={secondarySupplier.transferBasisId}
                                                isLoading={props.transferBasisLoading}
                                                label={translateString("transferBasis")}
                                                placeholder={translateString("transferBasis")}
                                                disabled={props.readOnly}
                                                options={filterTransferBasis(props.transferBasisData, selectedSecondarySupplier?.countryCode)}
                                                toolTipText={translateString("subprocessorSpecifyTransferBasis")}
                                                errorText={
                                                    props.validationErrors.find((v) => v.field === `${props.supplierId}${secondarySupplier.id}transferBasisId`)
                                                        ?.error
                                                }
                                                onChange={(transferBasis) => secondarySupplierUpdater.onTransferBasisChange(transferBasis)}
                                                noOptionsLabel={translateString("noOptions")}
                                            />
                                        )}
                                        {showTia(
                                            props.legalEntityData,
                                            secondarySupplier.dataProcessorId,
                                            hasTiaAndTransferBasis,
                                            secondarySupplier.transferBasisId
                                        ) && (
                                            <DotLegalSelect
                                                disableClearable
                                                selectedItem={getSelectedDocument(
                                                    secondarySupplier.transferImpactAssessmentId,
                                                    secondarySupplier.hasTransferImpactAssessment,
                                                    secondarySupplier.containsNotAccessibleTia
                                                )}
                                                isLoading={props.documentsLoading}
                                                label={translateString("tia")}
                                                disabled={props.readOnly}
                                                placeholder={translateString("tia")}
                                                options={getSelectableTiaDocumentItems(
                                                    filterDocumentDataByCompanyId(props.documentsData, secondarySupplier.dataProcessorId),
                                                    translateString,
                                                    secondarySupplier.hasTransferImpactAssessment,
                                                    secondarySupplier.containsNotAccessibleTia
                                                )}
                                                toolTipText={translateString("tiaTooltip")}
                                                errorText={
                                                    props.validationErrors.find(
                                                        (v) => v.field === `${props.supplierId}${secondarySupplier.id}transferImpactAssessment`
                                                    )?.error
                                                }
                                                onChange={(tia) => secondarySupplierUpdater.onTransferImpactAssessmentChange(tia)}
                                                noOptionsLabel={translateString("noOptions")}
                                            />
                                        )}
                                    </React.Fragment>
                                )}

                                {hasStorageLocationPermission && (
                                    <DotLegalMultiSelect
                                        isLoading={props.countriesLoading}
                                        options={props.countryList}
                                        chosenOptions={props.secondarySupplier.dataStorageLocations}
                                        disabled={props.readOnly}
                                        placeholder={translateString("storageLocation")}
                                        label={translateString("storageLocation")}
                                        toolTipText={translateString("secondarySupplierStorageTooltip")}
                                        onChange={(countries) => secondarySupplierUpdater.onDataStorageLocationChange(countries)}
                                        noOptionsLabel={translateString("noOptions")}
                                    />
                                )}

                                {hasSupportAccessPermission && (
                                    <DotLegalMultiSelect
                                        isLoading={props.countriesLoading}
                                        options={props.countryList}
                                        chosenOptions={props.secondarySupplier.supportAccessLocations}
                                        disabled={props.readOnly}
                                        placeholder={translateString("supportAccess")}
                                        label={translateString("supportAccess")}
                                        toolTipText={translateString("secondarySupplierSupportTooltip")}
                                        onChange={(countries) => secondarySupplierUpdater.onSupportAccesssLocationChange(countries)}
                                        noOptionsLabel={translateString("noOptions")}
                                    />
                                )}
                            </React.Fragment>
                        )}
                    </SegmentStep>
                </Box>
            </Box>
        </React.Fragment>
    );
}

export default SystemSecondarySupplier;
